import { api } from "@/lib/api"

/**
 * Cobranzas.
 *
 *   GET    /cobranza/deudores      la cartera, con su antigüedad y qué toca hoy
 *   GET    /cobranza/agenda        qué toca cada día de las próximas semanas
 *   POST   /cobranza/seguimiento   manda el toque que corresponde, y lo registra
 *   PUT    /cobranza/incobrable/{id}    da una deuda por incobrable (con motivo)
 *   DELETE /cobranza/incobrable/{id}    la devuelve a la cartera
 */

export type Situacion = "por_vencer" | "vence_hoy" | "vencida" | "cobrada" | "sin_fecha"
export type TipoDeToque = "antes" | "vispera" | "vencimiento" | "seguimiento" | "escalamiento"

export interface Seguimiento {
  tipo: TipoDeToque
  fecha: string
  /** `enviado` | `sin_transporte` | `sin_destinatario` | `fallido` | `registrado`. */
  resultado: string
}

export interface Deudor {
  id: string
  numero: string | null
  documento_fiscal: string | null
  cliente: string | null
  /**
   * A DÓNDE SE LE RECLAMA, desde la ficha del cliente (`party.email_cobranza`).
   *
   * `null` cuando el cliente no está en el maestro o no tiene correo puesto. La
   * pantalla lo dice y enlaza a su ficha: antes había un campo de texto POR FILA
   * pidiéndolo, así que el dato no se recordaba de un mes para otro y cada
   * persona tecleaba una dirección distinta. Ver la migración 0024.
   */
  email_cobranza: string | null
  concepto: string | null
  pais: string
  sociedad: string | null
  entity_id: string | null
  moneda: string | null
  importe: number
  importe_usd: number
  fecha_emision: string | null
  /** DERIVADA: la factura no la guarda. Ver `cadencia.vencimiento`. */
  fecha_vencimiento: string | null
  fecha_pago: string | null
  estado: string | null
  situacion: Situacion
  /** `null` cuando no hay fecha de emisión. NO es cero. */
  dias_vencido: number | null
  tramo: string
  /** Umbral de días, NO una decisión contable. Ver `incobrable`. */
  probable_incobrable: boolean
  /** DECIDIDO por una persona, con motivo. El sistema nunca lo pone solo. */
  incobrable: boolean
  incobrable_desde: string | null
  incobrable_motivo: string | null
  /**
   * EL ESCALÓN DE ANTES (migración 0026): alguien ya está hablando con este
   * cliente. Mientras esté puesto, la cadencia automática no le escribe — un
   * recordatorio a un cliente con el que se acordó ayer un plan de pagos es la
   * forma más rápida de que se caiga el acuerdo.
   *
   * NO sale de la cartera ni deja de contar como deuda: sigue debiendo.
   */
  en_gestion: boolean
  en_gestion_desde: string | null
  en_gestion_nota: string | null
  seguimientos: Seguimiento[]
  ultimo_contacto: string | null
  toca_hoy: { tipo: TipoDeToque; motivo: string; fecha: string } | null
}

export interface Cartera {
  hoy: string
  deudores: Deudor[]
  por_tramo: Record<string, { facturas: number; importe_usd: number }>
  total_usd: number
  con_toque_hoy: number
  probables_incobrables: number
  declarados_incobrables: number
  en_gestion: number
  en_gestion_usd: number
  orden: string
  ordenes: Record<string, string>
  politica: Record<string, unknown>
  nota: string
}

export type Orden = "vencimiento" | "monto" | "riesgo" | "cliente"

export function fetchCartera(params: {
  pais?: string
  orden?: Orden
  incluirCobradas?: boolean
}): Promise<Cartera> {
  return api.get<Cartera>("/cobranza/deudores", {
    query: {
      pais: params.pais || undefined,
      orden: params.orden,
      incluir_cobradas: params.incluirCobradas ? "true" : undefined,
    },
  })
}

export interface Agenda {
  desde: string
  hasta: string
  por_dia: Record<string, Array<{
    invoice_id: string
    cliente: string | null
    numero: string | null
    sociedad: string | null
    importe_usd: number
    tipo: TipoDeToque
    motivo: string
  }>>
  total_toques: number
  nota: string
}

export function fetchAgenda(dias = 14): Promise<Agenda> {
  return api.get<Agenda>("/cobranza/agenda", { query: { dias } })
}

export interface ResultadoSeguimiento {
  invoice_id: string
  cliente?: string | null
  tipo?: TipoDeToque
  resultado: string
  detalle?: string | null
  asunto?: string
  cuerpo?: string
  destinatario?: string | null
  /** La plantilla propia falló al rellenarse y se mandó la de Marty. */
  plantilla_rota?: string | null
}

/**
 * Manda el seguimiento. `destinatarios` va por `invoice_id` o por nombre de
 * cliente: la factura NO guarda el correo, así que viaja desde la pantalla y se
 * registra con el envío — si mañana el cliente cambia de correo, el histórico
 * tiene que seguir diciendo a dónde fue el aviso de marzo.
 */
export function enviarSeguimiento(payload: {
  invoice_ids: string[]
  tipo?: TipoDeToque
  destinatarios?: Record<string, string>
}): Promise<{
  resultados: ResultadoSeguimiento[]
  enviados: number
  transporte: string
  aviso: string | null
}> {
  return api.post("/cobranza/seguimiento", payload)
}

export function declararIncobrable(id: string, motivo: string): Promise<{
  id: string
  incobrable: boolean
  aviso: string
}> {
  return api.put(`/cobranza/incobrable/${id}`, { motivo })
}

export function revertirIncobrable(id: string): Promise<{ id: string }> {
  return api.delete(`/cobranza/incobrable/${id}`)
}

// ===========================================================================
// LAS PLANTILLAS, EDITABLES (migración 0026) — punto 13b
//
//   GET    /cobranza/plantillas                 los cinco textos
//   PUT    /cobranza/plantillas                 guarda uno propio
//   DELETE /cobranza/plantillas/{tipo}          vuelve al de Marty
//   POST   /cobranza/plantillas/vista-previa    cómo queda, sin guardar
// ===========================================================================

/** El idioma DEL DESTINATARIO —el cliente del cliente—, no el de la interfaz. */
export type IdiomaDelCorreo = "es" | "pt" | "en"

export interface PlantillaDeCobranza {
  tipo: TipoDeToque
  idioma: IdiomaDelCorreo
  /** Cuándo se manda este toque, en palabras. */
  cuando: string
  asunto: string
  cuerpo: string
  /** El texto de fábrica, para poder comparar y volver. */
  asunto_de_marty: string
  cuerpo_de_marty: string
  /** `false` = está usando el de Marty y recibirá sus mejoras. */
  propia: boolean
  actualizado_en: string | null
  actualizado_por: string | null
}

export function fetchPlantillas(idioma: IdiomaDelCorreo = "es"): Promise<{
  idioma: IdiomaDelCorreo
  plantillas: PlantillaDeCobranza[]
  /** Los datos que Marty sabe rellenar, con qué es cada uno. */
  marcadores: Record<string, string>
  nota: string
  aviso: string
}> {
  return api.get("/cobranza/plantillas", { query: { idioma } })
}

export function guardarPlantilla(body: {
  tipo: TipoDeToque
  idioma: IdiomaDelCorreo
  asunto: string
  cuerpo: string
}): Promise<{
  tipo: string
  propia: boolean
  vista_previa: { asunto: string; cuerpo: string }
  mensaje: string
}> {
  return api.put("/cobranza/plantillas", body)
}

export function borrarPlantilla(tipo: TipoDeToque, idioma: IdiomaDelCorreo) {
  return api.delete<{ mensaje: string }>(`/cobranza/plantillas/${tipo}`,
                                         { query: { idioma } })
}

/**
 * Cómo queda con datos de ejemplo, SIN guardar.
 *
 * Un texto de cobranza se escribe mirando el resultado, no la plantilla: los
 * marcadores hacen que `{importe}` y «PEN 1.234,56» ocupen y se lean muy
 * distinto.
 */
export function previsualizarPlantilla(body: {
  tipo: TipoDeToque
  idioma: IdiomaDelCorreo
  asunto: string
  cuerpo: string
}): Promise<{
  asunto: string
  cuerpo: string
  /** Qué impide guardarla. Vacío = se puede. */
  problemas: string[]
  se_puede_guardar: boolean
}> {
  return api.post("/cobranza/plantillas/vista-previa", body)
}

// ===========================================================================
// EN GESTIÓN: el escalón entre normal e incobrable
// ===========================================================================

/**
 * LA NOTA NO ES OBLIGATORIA, y el motivo del incobrable sí.
 *
 * La diferencia es deliberada: marcar «estoy hablando con ellos» tiene que
 * costar un clic o nadie lo va a marcar, y sin la marca el sistema le manda el
 * recordatorio automático al cliente con el que se acaba de acordar algo.
 * Declarar una deuda perdida es otra cosa: lleva provisión contable.
 */
/**
 * LA CADENCIA DEL CLIENTE, con las claves que el motor lee. Ver `CadenciaDialog`.
 * Va a `/masters/policies/collection` (política del tenant, `party_id` nulo):
 * crear una cierra la vigente del mismo alcance.
 */
export interface Cadencia {
  dias_credito: number
  avisos_previos: number[]
  aviso_el_dia: boolean
  dias_de_seguimiento: number[]
  escalar_a_los_dias: number
  alerta_incobrable_a_los_dias: number
}

export function guardarCadencia(c: Cadencia, motivo: string): Promise<{ id: string; vigente_desde: string }> {
  const { dias_credito, ...cadencia } = c
  return api.post("/masters/policies/collection", {
    motivo,
    dias_credito,
    cadencia_recordatorios: cadencia,
  })
}

export function marcarEnGestion(invoiceId: string, nota?: string) {
  return api.put<{ en_gestion: boolean; aviso: string }>(
    `/cobranza/en-gestion/${invoiceId}`, { nota: nota || null })
}

export function quitarDeGestion(invoiceId: string) {
  return api.delete<{ en_gestion: boolean; aviso: string }>(
    `/cobranza/en-gestion/${invoiceId}`)
}
